import { useStore } from '@nanostores/react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { atom } from 'nanostores';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PlainButton } from '@customerio/pluma-components/react';
import { useLocalStorage } from '../../hooks/browser/use-local-storage';
import { FeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { SIDEBAR_PORTAL_FADE_ANIMATE, sidebarPortalFadeInitial, sidebarPortalFadeTransition } from '@/utils/motion';
import clsx from 'clsx';
import * as styles from './index.css';

const DEFAULT_SIDEBAR_WIDTH = 370;
const MIN_SIDEBAR_WIDTH = 370;
const MAX_SIDEBAR_WIDTH = 740;
const SIDEBAR_ANIMATION_DURATION = 150;
/** Brief delay so section exit starts, then width close overlaps for a quick close without blank full-width pause. */
const SECTION_EXIT_DURATION_MS = 100;
const targetId = 'sidebar-portal';
const activeId = atom<string | null>(null);
const closingId = atom<string | null>(null);

export function open(id: string) {
  activeId.set(id);
  closingId.set(null);
}

export async function close() {
  closingId.set(activeId.get());
  // Wait for section exit animations (e.g. help sidebar stagger) so they are visible before the panel closes.
  await new Promise((resolve) => setTimeout(resolve, SECTION_EXIT_DURATION_MS));
  activeId.set(null);
  await new Promise((resolve) => setTimeout(resolve, SIDEBAR_ANIMATION_DURATION));
  closingId.set(null);
}

export function toggle(id: string) {
  if (activeId.get() === id) {
    close();
  } else {
    open(id);
  }
}

// expose the sidebar functions to the window object
// so that the ember ui can hook into it and open/close the sidebar
// to prevent having two sidebars open at the same time
Object.assign(window, {
  __hydra_sidebar__: {
    open,
    close,
    toggle,
    subscribe: activeId.subscribe,
    openSupportForm: () => {
      // No-op placeholder. Replaced at runtime by HelpSidebar once it mounts.
    },
  } satisfies Window['__hydra_sidebar__'],
});

/**
 * Renders the sidebar root element.
 * This is the element that will be used as the target for the sidebar portal.
 */
export function SidebarRoot() {
  const _activeSidebarId = useStore(activeId);
  const isOpen = _activeSidebarId !== null;
  const shouldReduceMotion = useReducedMotion();
  const [display, setDisplay] = useState<'block' | 'none'>('none');
  const [sidebarWidth, setSidebarWidth] = useLocalStorage('sidebar-width', DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const is2026LayoutRefresh = useFeatureFlag(FeatureFlag.LayoutRefresh2026);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizing || sidebarRef.current == null) return;

      const rect = sidebarRef.current.getBoundingClientRect();
      const newWidth = rect.right - event.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, setSidebarWidth]);

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault();
    setIsResizing(true);
  };

  return (
    <>
      {isOpen && is2026LayoutRefresh ? (
        <PlainButton
          className={styles.resizeHandleLayoutRefresh}
          onMouseDown={handleMouseDown}
          aria-label="Resize sidebar"
        />
      ) : null}
      <AnimatePresence>
        <motion.div
          ref={sidebarRef}
          animate={{
            width: isOpen ? sidebarWidth : 0,
            display,
          }}
          role="complementary"
          initial={{ width: 0 }}
          // putting 0 causes a slight scroll on the html element
          // when the sidebar is closed, so we use display: none instead
          // and set the width to 0
          onAnimationStart={() => {
            if (isOpen) {
              setDisplay('block');
            }
          }}
          onAnimationComplete={() => {
            if (!isOpen) {
              setDisplay('none');
            }
          }}
          exit={{
            display: 'none',
          }}
          transition={{
            duration: isResizing || shouldReduceMotion === true ? 0 : SIDEBAR_ANIMATION_DURATION / 1000,
            ease: 'easeInOut',
          }}
          style={{
            zIndex: 40, // pluma navbar z-index
            position: 'relative',
          }}
          className={clsx(styles.sidebarRoot, is2026LayoutRefresh ? styles.sidebarRootLayoutRefresh : null)}
          id={targetId}
        >
          {isOpen && !is2026LayoutRefresh ? (
            <PlainButton className={styles.resizeHandle} onMouseDown={handleMouseDown} aria-label="Resize sidebar" />
          ) : null}
        </motion.div>
      </AnimatePresence>
    </>
  );
}

export const Sidebar = forwardRef<
  HTMLDivElement,
  {
    id: string;
    children: React.ReactNode;
    onToggle?: (isOpen: boolean) => void;
    onToggleComplete?: (isOpen: boolean) => void;
  }
>(function Sidebar({ id, children, onToggle, onToggleComplete }, ref) {
  const _activeSidebarId = useStore(activeId);
  const _closingSidebarId = useStore(closingId);
  const isClosing = _closingSidebarId === id;
  const shouldReduceMotion = useReducedMotion();
  const sidebarTarget = document.getElementById(targetId);
  const lastActiveId = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

  useEffect(() => {
    const wasActive = lastActiveId.current === id;
    const isActive = _activeSidebarId === id;

    if (lastActiveId.current !== _activeSidebarId) {
      lastActiveId.current = _activeSidebarId;

      if (wasActive && !isActive) {
        onToggle?.(false);
      }

      if (!wasActive && isActive) {
        onToggle?.(true);
      }
    }
  }, [_activeSidebarId, id, onToggle]);

  if (_activeSidebarId !== id && _closingSidebarId !== id) {
    return null;
  }

  if (sidebarTarget == null) {
    return null;
  }

  // Fade only on the portal root so nested sidebars can own vertical stagger without compounding motion.
  const transition = sidebarPortalFadeTransition(isClosing, shouldReduceMotion);
  const sidebarContentInitial = sidebarPortalFadeInitial(shouldReduceMotion);

  return createPortal(
    <motion.div
      ref={containerRef}
      className={styles.sidebar}
      initial={sidebarContentInitial}
      animate={SIDEBAR_PORTAL_FADE_ANIMATE}
      transition={transition}
      onAnimationComplete={() => {
        onToggleComplete?.(!isClosing);
      }}
    >
      {children}
    </motion.div>,
    sidebarTarget,
  );
});

export function useSidebarActive(id: string) {
  const active = useStore(activeId) === id;
  const closing = useStore(closingId) === id;

  return active || closing;
}

/**
 * Returns whether the sidebar with the given id is currently in its closing state
 * (after close() was called but before the close animation completes).
 */
export function useSidebarClosing(id: string) {
  return useStore(closingId) === id;
}

export { SidebarHeader } from './sidebar-header';
